import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientProfileStore } from '../../store/clientProfileStore';
import { useAuthStore } from '../../store/authStore';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../../components/shared/PullToRefreshIndicator';
import { telegramLinks } from '../../utils/telegramLinks';
import { useHaptic } from '../../hooks/useHaptic';
import { ErrorRetry } from '../../components/client/ui/ErrorRetry';
import { ProfileSkeleton } from '../../components/client/profile/ProfileSkeleton';
import { ProfileHeader } from '../../components/client/profile/ProfileHeader';
import { AccountCard } from '../../components/client/profile/AccountCard';
import { ContactActionsCard } from '../../components/client/profile/ContactActionsCard';
import { BusinessCard } from '../../components/client/profile/BusinessCard';
import { PoliciesCard } from '../../components/client/profile/PoliciesCard';
import { HelpCard } from '../../components/client/profile/HelpCard';
import { EditNameModal } from '../../components/client/profile/EditNameModal';
import { LanguageSwitcher } from '../../components/client/profile/LanguageSwitcher';
import { useLanguageStore } from '../../store/languageStore';

export default function ProfileTab() {
  const { t, i18n } = useTranslation('customer');
  const { data, isLoading, error, fetchProfile, refetch, setClientName } = useClientProfileStore();
  const tenantId = useAuthStore((s) => s.session?.tenantContext.tenantId ?? null);
  const setUser = useAuthStore((s) => s.setUser);
  const haptic = useHaptic();
  const { supportedLanguages } = useLanguageStore();

  const [editNameOpen, setEditNameOpen] = useState(false);

  const supportTelegram = (import.meta.env.VITE_SUPPORT_TELEGRAM as string | undefined) ?? null;

  useEffect(() => { void fetchProfile(); }, []); // mount-only fetch

  // Re-fetch profile when user switches interface language so tenant-translated
  // content (business name, address, policies, schedule labels) is refreshed.
  useEffect(() => {
    const handler = () => { void refetch(); };
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, [i18n, refetch]);

  const { containerRef, pullDistance, isRefreshing, progress } =
    usePullToRefresh<HTMLDivElement>({ onRefresh: refetch, disabled: isLoading });

  const handleEditNamePress = () => {
    if (tenantId === null) return;
    haptic.impactLight();
    setEditNameOpen(true);
  };

  return (
    <div ref={containerRef} style={{ height: '100%', overflowY: 'auto', position: 'relative' }}>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} progress={progress} />
      {isLoading && data === null ? <ProfileSkeleton />
      : error !== null && data === null ? <ErrorRetry message={error ?? t('profile.loadError')} onRetry={refetch} />
      : (
    <div style={{ paddingBottom: 'calc(60px + env(safe-area-inset-bottom, 0px))' }}>

      {data !== null && (
        <>
          <ProfileHeader
            name={data.client.name}
            telegramUsername={data.client.telegramUsername}
            onEditName={tenantId !== null ? handleEditNamePress : undefined}
          />

          <AccountCard
            phone={data.client.phone}
            telegramUsername={data.client.telegramUsername}
          />

          <LanguageSwitcher supportedLanguages={supportedLanguages} />

          <ContactActionsCard
            phone={data.business.phone}
            telegramContact={data.business.telegramContact}
            onPhoneCall={() => {
              haptic.impactLight();
              if (data.business.phone !== null) telegramLinks.openPhoneCall(data.business.phone);
            }}
            onTelegramUser={() => {
              haptic.impactLight();
              if (data.business.telegramContact !== null) telegramLinks.openTelegramUser(data.business.telegramContact);
            }}
          />

          <BusinessCard
            name={data.business.name}
            subtitle={data.business.subtitle}
            address={data.business.address}
            workingHours={data.business.workingHours}
            phone={data.business.phone}
            mapUrl={data.business.mapUrl}
            onMapOpen={() => {
              if (data.business.mapUrl !== null) telegramLinks.openMap(data.business.mapUrl);
            }}
            onPhoneCall={() => {
              haptic.impactLight();
              if (data.business.phone !== null) telegramLinks.openPhoneCall(data.business.phone);
            }}
          />

          <PoliciesCard
            cancellation={data.policies.cancellation}
            payment={data.policies.payment}
            lateArrival={data.policies.lateArrival}
          />

          {supportTelegram !== null && (
            <HelpCard
              onOpen={() => {
                haptic.impactLight();
                telegramLinks.openTelegramUser(supportTelegram);
              }}
            />
          )}
        </>
      )}
    </div>
      )}

      {tenantId !== null && (
        <EditNameModal
          open={editNameOpen}
          initialName={data?.client.name ?? null}
          tenantId={tenantId}
          onSuccess={(updated) => {
            setUser({
              firstName: updated.firstName,
              lastName: updated.lastName,
            });
            const newName =
              updated.displayName ?? updated.firstName ?? data?.client.name ?? '';
            if (newName !== '') setClientName(newName);
            setEditNameOpen(false);
          }}
          onClose={() => setEditNameOpen(false)}
        />
      )}
    </div>
  );
}
